import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, TrendingDown, ArrowRight, BarChart3, PieChart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

const mockMarketData = [
  { symbol: 'SCOM', name: 'Safaricom', price: 23.45, change: 2.5, volume: '5.2M' },
  { symbol: 'EABL', name: 'East African Breweries', price: 165.00, change: -1.2, volume: '1.1M' },
  { symbol: 'KCB', name: 'KCB Group', price: 32.80, change: 0.8, volume: '3.4M' },
  { symbol: 'EQTY', name: 'Equity Group', price: 45.25, change: 1.5, volume: '2.8M' },
];

export default function MarketsPage() {
  const { token } = useAuth();
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');

  useEffect(() => {
    fetchInvestments();
  }, [category]);

  const fetchInvestments = async () => {
    try {
      const params = category !== 'all' ? `?category=${category}` : '';
      const response = await axios.get(`${API_URL}/api/investments${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvestments(response.data.investments || []);
    } catch (error) {
      console.error('Investments fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout title="Jambo Markets">
      <div className="p-4 lg:p-6 space-y-6">
        {/* Market Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">NSE 20</p>
              <p className="text-xl font-bold">1,847.23</p>
              <p className="text-sm text-green-500 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> +1.24%
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">NSE All Share</p>
              <p className="text-xl font-bold">112.45</p>
              <p className="text-sm text-green-500 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> +0.87%
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500/10 to-amber-500/5">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">USD/KES</p>
              <p className="text-xl font-bold">154.25</p>
              <p className="text-sm text-red-500 flex items-center gap-1">
                <TrendingDown className="h-3 w-3" /> -0.15%
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">T-Bill 91 Day</p>
              <p className="text-xl font-bold">16.23%</p>
              <p className="text-sm text-green-500 flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> +0.05%
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Live Market */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Live Market
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-muted-foreground border-b">
                    <th className="pb-3 font-medium">Symbol</th>
                    <th className="pb-3 font-medium">Name</th>
                    <th className="pb-3 font-medium text-right">Price</th>
                    <th className="pb-3 font-medium text-right">Change</th>
                    <th className="pb-3 font-medium text-right">Volume</th>
                    <th className="pb-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {mockMarketData.map((stock) => (
                    <tr key={stock.symbol} className="hover:bg-muted/50" data-testid={`stock-${stock.symbol}`}>
                      <td className="py-4 font-semibold">{stock.symbol}</td>
                      <td className="py-4 text-muted-foreground">{stock.name}</td>
                      <td className="py-4 text-right font-medium">{stock.price.toFixed(2)}</td>
                      <td className={`py-4 text-right font-medium ${stock.change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {stock.change >= 0 ? '+' : ''}{stock.change}%
                      </td>
                      <td className="py-4 text-right text-muted-foreground">{stock.volume}</td>
                      <td className="py-4 text-right">
                        <Button size="sm" variant="outline" className="rounded-full">
                          Trade
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Investment Products */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Investment Products</h2>
          </div>

          <Tabs value={category} onValueChange={setCategory} className="mb-6">
            <TabsList>
              <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
              <TabsTrigger value="equities" data-testid="tab-equities">Equities</TabsTrigger>
              <TabsTrigger value="etf" data-testid="tab-etf">ETFs</TabsTrigger>
              <TabsTrigger value="private_debt" data-testid="tab-debt">Private Debt</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              Array(6).fill(0).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <Skeleton className="h-5 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-full mb-4" />
                    <Skeleton className="h-8 w-1/3" />
                  </CardContent>
                </Card>
              ))
            ) : investments.length ? (
              investments.map((investment) => (
                <Card 
                  key={investment.id} 
                  className="card-hover investment-gradient"
                  data-testid={`investment-card-${investment.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <PieChart className="h-6 w-6 text-primary" />
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        investment.category === 'equities' ? 'bg-blue-100 text-blue-700' :
                        investment.category === 'etf' ? 'bg-purple-100 text-purple-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {investment.category.toUpperCase()}
                      </span>
                    </div>
                    <h3 className="font-semibold text-lg mb-1">{investment.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{investment.description}</p>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Expected Return</p>
                        <p className="text-xl font-bold text-green-500">+{investment.return_rate}%</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Min. Investment</p>
                        <p className="font-semibold">{formatCurrency(investment.min_investment)}</p>
                      </div>
                    </div>
                    <Button className="w-full rounded-full" data-testid={`invest-${investment.id}`}>
                      Invest Now
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full py-20 text-center text-muted-foreground">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No investments found</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </AppLayout>
  );
}
